import React, { PropsWithChildren, useContext } from 'react';

import type { AttachmentProps } from '../components/Attachment/Attachment';
import type { AvatarProps } from '../components/Avatar/Avatar';
import type { DateSeparatorProps } from '../components/DateSeparator/DateSeparator';
import type { EmptyStateIndicatorProps } from '../components/EmptyStateIndicator/EmptyStateIndicator';
import type { EventComponentProps } from '../components/EventComponent/EventComponent';
import type { LoadingIndicatorProps } from '../components/Loading/LoadingIndicator';
import type { FixedHeightMessageProps } from '../components/Message/FixedHeightMessage';
import type { MessageUIComponentProps, PinIndicatorProps } from '../components/Message/types';
import type { MessageDeletedProps } from '../components/Message/MessageDeleted';
import type { GiphyPreviewMessageProps } from '../components/MessageList/GiphyPreviewMessage';
import type { MessageListNotificationsProps } from '../components/MessageList/MessageListNotifications';
import type { MessageNotificationProps } from '../components/MessageList/MessageNotification';
import type { MessageOptionsProps } from '../components/Message/MessageOptions';
import type { EmojiSearchIndex, MessageInputProps } from '../components/MessageInput/MessageInput';
import type { QuotedMessagePreviewProps } from '../components/MessageInput/QuotedMessagePreview';
import type { MessageProps } from '../components/Message/types';
import type { MessageRepliesCountButtonProps } from '../components/Message/MessageRepliesCountButton';
import type { MessageStatusProps } from '../components/Message/MessageStatus';
import type { MessageTimestampProps } from '../components/Message/MessageTimestamp';
import type { ModalGalleryProps } from '../components/Gallery/ModalGallery';
import type { ReactionSelectorProps } from '../components/Reactions/ReactionSelector';
import type { ReactionsListProps } from '../components/Reactions/ReactionsList';
import type {
  SuggestionItemProps,
  SuggestionListProps,
} from '../components/ChatAutoComplete/ChatAutoComplete';
import { UnreadMessagesSeparatorProps } from '../components/MessageList/UnreadMessagesSeparator';
import type { SuggestionListHeaderProps } from '../components/AutoCompleteTextarea';
import type { SendButtonProps } from '../components/MessageInput/icons';
import type { ThreadHeaderProps } from '../components/Thread/ThreadHeader';
import type { TypingIndicatorProps } from '../components/TypingIndicator/TypingIndicator';

import type { CustomTrigger, DefaultBeloChatGenerics, UnknownType } from '../types/types';
import type {
  BaseImageProps,
  CooldownTimerProps,
  CustomMessageActionsListProps,
} from '../components';
import type { LinkPreviewListProps } from '../components/MessageInput/LinkPreviewList';
import type { ReactionOptions } from '../components/Reactions/reactionOptions';
import type { MessageBouncePromptProps } from '../components/MessageBounce';
import type { UnreadMessagesNotificationProps } from '../components/MessageList/UnreadMessagesNotification';

export type ComponentContextValue<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics,
  V extends CustomTrigger = CustomTrigger
> = {
  Attachment: React.ComponentType<AttachmentProps<BeloChatGenerics>>;
  DateSeparator: React.ComponentType<DateSeparatorProps>;
  Message: React.ComponentType<MessageUIComponentProps<BeloChatGenerics>>;
  MessageSystem: React.ComponentType<EventComponentProps<BeloChatGenerics>>;
  reactionOptions: ReactionOptions;
  UnreadMessagesSeparator: React.ComponentType<UnreadMessagesSeparatorProps>;
  AttachmentPreviewList?: React.ComponentType;
  AutocompleteSuggestionHeader?: React.ComponentType<SuggestionListHeaderProps>;
  AutocompleteSuggestionItem?: React.ComponentType<SuggestionItemProps<BeloChatGenerics>>;
  AutocompleteSuggestionList?: React.ComponentType<SuggestionListProps<BeloChatGenerics>>;
  Avatar?: React.ComponentType<AvatarProps<BeloChatGenerics>>;
  BaseImage?: React.ComponentType<BaseImageProps>;
  CooldownTimer?: React.ComponentType<CooldownTimerProps>;
  CustomMessageActionsList?: React.ComponentType<CustomMessageActionsListProps<BeloChatGenerics>>;
  EditMessageInput?: React.ComponentType<MessageInputProps<BeloChatGenerics>>;
  EmojiPicker?: React.ComponentType;
  emojiSearchIndex?: EmojiSearchIndex;
  EmptyStateIndicator?: React.ComponentType<EmptyStateIndicatorProps>;
  FileUploadIcon?: React.ComponentType;
  GiphyPreviewMessage?: React.ComponentType<GiphyPreviewMessageProps<BeloChatGenerics>>;
  HeaderComponent?: React.ComponentType;
  Input?: React.ComponentType<MessageInputProps<BeloChatGenerics, V>>;
  LinkPreviewList?: React.ComponentType<LinkPreviewListProps>;
  LoadingIndicator?: React.ComponentType<LoadingIndicatorProps>;
  MessageBouncePrompt?: React.ComponentType<MessageBouncePromptProps>;
  MessageDeleted?: React.ComponentType<MessageDeletedProps<BeloChatGenerics>>;
  MessageListNotifications?: React.ComponentType<MessageListNotificationsProps>;
  MessageNotification?: React.ComponentType<MessageNotificationProps>;
  MessageOptions?: React.ComponentType<MessageOptionsProps<BeloChatGenerics>>;
  MessageRepliesCountButton?: React.ComponentType<MessageRepliesCountButtonProps>;
  MessageStatus?: React.ComponentType<MessageStatusProps>;
  MessageTimestamp?: React.ComponentType<MessageTimestampProps<BeloChatGenerics>>;
  ModalGallery?: React.ComponentType<ModalGalleryProps>;
  PinIndicator?: React.ComponentType<PinIndicatorProps<BeloChatGenerics>>;
  QuotedMessage?: React.ComponentType;
  QuotedMessagePreview?: React.ComponentType<QuotedMessagePreviewProps<BeloChatGenerics>>;
  ReactionSelector?: React.ForwardRefExoticComponent<ReactionSelectorProps<BeloChatGenerics>>;
  ReactionsList?: React.ComponentType<ReactionsListProps<BeloChatGenerics>>;
  SendButton?: React.ComponentType<SendButtonProps<BeloChatGenerics>>;
  ThreadHead?: React.ComponentType<MessageProps<BeloChatGenerics>>;
  ThreadHeader?: React.ComponentType<ThreadHeaderProps<BeloChatGenerics>>;
  ThreadInput?: React.ComponentType<MessageInputProps<BeloChatGenerics, V>>;
  ThreadStart?: React.ComponentType;
  TriggerProvider?: React.ComponentType;
  TypingIndicator?: React.ComponentType<TypingIndicatorProps>;
  UnreadMessagesNotification?: React.ComponentType<UnreadMessagesNotificationProps>;
  VirtualMessage?: React.ComponentType<FixedHeightMessageProps<BeloChatGenerics>>;
};

export const ComponentContext = React.createContext<ComponentContextValue | undefined>(undefined);

export const ComponentProvider = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics,
  V extends CustomTrigger = CustomTrigger
>({
  children,
  value,
}: PropsWithChildren<{
  value: Partial<ComponentContextValue<BeloChatGenerics, V>>;
}>) => (
  <ComponentContext.Provider value={(value as unknown) as ComponentContextValue}>
    {children}
  </ComponentContext.Provider>
);

export const useComponentContext = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics,
  V extends CustomTrigger = CustomTrigger
>(
  componentName?: string,
) => {
  const contextValue = useContext(ComponentContext);

  if (!contextValue) {
    console.warn(
      `The useComponentContext hook was called outside of the ComponentContext provider. Make sure this hook is called within a child of the Channel component. The errored call is located in the ${componentName} component.`,
    );

    return {} as ComponentContextValue<BeloChatGenerics, V>;
  }

  return contextValue as ComponentContextValue<BeloChatGenerics, V>;
};

/**
 * Typescript currently does not support partial inference, so if ComponentContext
 * typing is desired while using the HOC withComponentContext, the Props for the
 * wrapped component must be provided as the first generic.
 */
export const withComponentContext = <
  P extends UnknownType,
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics,
  V extends CustomTrigger = CustomTrigger
>(
  Component: React.ComponentType<P>,
) => {
  const WithComponentContextComponent = (
    props: Omit<P, keyof ComponentContextValue<BeloChatGenerics, V>>,
  ) => {
    const componentContext = useComponentContext<BeloChatGenerics, V>();

    return <Component {...(props as P)} {...componentContext} />;
  };

  WithComponentContextComponent.displayName = (
    Component.displayName ||
    Component.name ||
    'Component'
  ).replace('Base', '');

  return WithComponentContextComponent;
};
